import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import apiClient from '../api/client';
import './ImagensAleatorias.css';

/**
 * Página para admin gerenciar imagens aleatórias de nocaute
 */

const ImagensAleatorias = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [imagens, setImagens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState({ nocaute: false, eliminacao: false });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('todos'); // 'todos', 'nocaute', 'eliminacao'

  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }
    carregarImagens();
  }, [isAdmin, navigate]);

  const carregarImagens = async () => {
    try {
      setLoading(true);
      setError('');
      const params = filtroTipo !== 'todos' ? { tipo: filtroTipo } : {};
      const response = await apiClient.get('/imagens-nocautes-aleatorias', { params });
      setImagens(response.data || []);
    } catch (err) {
      console.error('Erro ao carregar imagens aleatórias:', err);
      setError(err.response?.data?.error || 'Erro ao carregar imagens aleatórias');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      carregarImagens();
    }
  }, [filtroTipo]);

  const handleFileSelect = async (tipo, event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validar formato (imagens e vídeos MP4)
    const allowedTypes = ['image/gif', 'image/webp', 'image/png', 'image/jpeg', 'image/jpg', 'video/mp4'];
    if (!allowedTypes.includes(file.type)) {
      setError('Formato não permitido. Use GIF, WebP, PNG, JPEG ou MP4.');
      return;
    }

    // Validar tamanho (16MB para vídeos, 10MB para imagens)
    const maxSize = file.type === 'video/mp4' ? 16 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxSize) {
      const maxSizeMB = file.type === 'video/mp4' ? '16MB' : '10MB';
      setError(`Arquivo muito grande. Tamanho máximo: ${maxSizeMB}.`);
      return;
    }

    try {
      setUploading({ ...uploading, [tipo]: true });
      setError('');
      setSuccess('');

      const formData = new FormData();
      formData.append('imagem', file);
      formData.append('tipo', tipo);

      // Debug: verificar FormData
      console.log('📤 Enviando upload aleatório:', {
        tipo: tipo,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
      });

      const response = await apiClient.post('/imagens-nocautes-aleatorias/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setSuccess(`Imagem de ${tipo === 'nocaute' ? 'nocaute' : 'eliminação'} adicionada!`);
      setTimeout(() => setSuccess(''), 5000);
      
      // Recarregar lista
      await carregarImagens();
    } catch (err) {
      console.error('Erro ao fazer upload:', err);
      setError(err.response?.data?.error || 'Erro ao fazer upload da imagem');
    } finally {
      setUploading({ ...uploading, [tipo]: false });
      // Limpar input
      event.target.value = '';
    }
  };

  const handleToggleAtivo = async (id) => {
    try {
      await apiClient.put(`/imagens-nocautes-aleatorias/${id}/toggle`);
      setSuccess('Status da imagem alterado!');
      setTimeout(() => setSuccess(''), 3000);
      await carregarImagens();
    } catch (err) {
      console.error('Erro ao alterar status:', err);
      setError(err.response?.data?.error || 'Erro ao alterar status da imagem');
    }
  };

  const handleDeletar = async (id) => {
    if (!window.confirm('Tem certeza que deseja deletar esta imagem?')) {
      return;
    }

    try {
      await apiClient.delete(`/imagens-nocautes-aleatorias/${id}`);
      setSuccess('Imagem deletada com sucesso!');
      setTimeout(() => setSuccess(''), 3000);
      await carregarImagens();
    } catch (err) {
      console.error('Erro ao deletar:', err);
      setError(err.response?.data?.error || 'Erro ao deletar imagem');
    }
  };

  const formatarTamanho = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  if (loading) {
    return <div className="container">Carregando imagens aleatórias...</div>;
  }

  if (!isAdmin) {
    return null;
  }

  const imagensFiltradas = filtroTipo === 'todos' 
    ? imagens 
    : imagens.filter(img => img.tipo === filtroTipo);

  const imagensNocaute = imagensFiltradas.filter(img => img.tipo === 'nocaute');
  const imagensEliminacao = imagensFiltradas.filter(img => img.tipo === 'eliminacao');

  return (
    <div className="container imagens-aleatorias-container">
      <div className="page-header">
        <h1>🎲 Imagens Aleatórias de Nocaute</h1>
        <button onClick={() => navigate('/admin/menu')} className="btn btn-secondary">
          ← Voltar
        </button>
      </div>

      {error && <div className="error">{error}</div>}
      {success && <div className="success">{success}</div>}

      <div className="info-box">
        <p>📸 Faça upload de imagens que serão usadas aleatoriamente quando um nocaute ou eliminação for registrado!</p>
        <p><strong>Formatos aceitos:</strong> GIF, WebP, PNG, JPEG, MP4 | <strong>Tamanho máximo:</strong> 10MB (imagens) / 16MB (vídeos)</p>
        <p><strong>ℹ️ Nota:</strong> Imagens serão redimensionadas automaticamente para 200x200px. Vídeos MP4 mantêm tamanho original.</p>
        <p><strong>ℹ️ Nota:</strong> Todas as imagens serão redimensionadas automaticamente para 200x200px.</p>
        <p><strong>⚠️ Importante:</strong> Para usar imagens aleatórias, configure o modo de envio como "Aleatório" na página de Configuração de Prêmios e Pontos.</p>
      </div>

      {/* Filtro */}
      <div className="filtro-container">
        <label>
          <strong>Filtrar por tipo:</strong>
          <select value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)}>
            <option value="todos">Todos</option>
            <option value="nocaute">Nocaute</option>
            <option value="eliminacao">Eliminação</option>
          </select>
        </label>
      </div>

      {/* Upload de Imagens */}
      <div className="upload-section">
        <div className="upload-card">
          <h2>🥊 Adicionar Imagem de Nocaute</h2>
          <label className="upload-label">
            <input
              type="file"
              accept=".gif,.webp,.png,.jpeg,.jpg,.mp4,image/gif,image/webp,image/png,image/jpeg,image/jpg,video/mp4"
              onChange={(e) => handleFileSelect('nocaute', e)}
              style={{ display: 'none' }}
              disabled={uploading.nocaute}
            />
            <div className="upload-placeholder">
              {uploading.nocaute ? (
                <div>⏳ Enviando...</div>
              ) : (
                <>
                  <div className="upload-icon">📤</div>
                  <div>Clique para fazer upload</div>
                  <div className="upload-hint">GIF, WebP, PNG ou JPEG (máx 10MB)</div>
                </>
              )}
            </div>
          </label>
        </div>

        <div className="upload-card">
          <h2>💀 Adicionar Imagem de Eliminação</h2>
          <label className="upload-label">
            <input
              type="file"
              accept=".gif,.webp,.png,.jpeg,.jpg,.mp4,image/gif,image/webp,image/png,image/jpeg,image/jpg,video/mp4"
              onChange={(e) => handleFileSelect('eliminacao', e)}
              style={{ display: 'none' }}
              disabled={uploading.eliminacao}
            />
            <div className="upload-placeholder">
              {uploading.eliminacao ? (
                <div>⏳ Enviando...</div>
              ) : (
                <>
                  <div className="upload-icon">📤</div>
                  <div>Clique para fazer upload</div>
                  <div className="upload-hint">GIF, WebP, PNG ou JPEG (máx 10MB)</div>
                </>
              )}
            </div>
          </label>
        </div>
      </div>

      {/* Lista de Imagens */}
      <div className="imagens-section">
        <h2>📚 Banco de Imagens ({imagensFiltradas.length} {imagensFiltradas.length === 1 ? 'imagem' : 'imagens'})</h2>

        {imagensFiltradas.length === 0 ? (
          <div className="empty-state">
            <p>Nenhuma imagem cadastrada ainda.</p>
            <p>Faça upload de imagens usando os campos acima.</p>
          </div>
        ) : (
          <div className="imagens-grid">
            {imagensFiltradas.map((imagem) => (
              <div key={imagem.id} className="imagem-card">
                <div className="imagem-preview-container">
                  <img
                    src={imagem.url}
                    alt={`Imagem de ${imagem.tipo}`}
                    className="imagem-preview"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }}
                  />
                  <div className="imagem-placeholder" style={{ display: 'none' }}>
                    Imagem não encontrada
                  </div>
                </div>
                <div className="imagem-info">
                  <div className="imagem-tipo">
                    {imagem.tipo === 'nocaute' ? '🥊' : '💀'} {imagem.tipo === 'nocaute' ? 'Nocaute' : 'Eliminação'}
                  </div>
                  <div className="imagem-detalhes">
                    {imagem.nome_arquivo} ({formatarTamanho(imagem.tamanho_arquivo)})
                  </div>
                  <div className="imagem-status">
                    Status: {imagem.ativo ? (
                      <span className="status-ativo">✅ Ativa</span>
                    ) : (
                      <span className="status-inativo">❌ Inativa</span>
                    )}
                  </div>
                  {imagem.uploadador && (
                    <div className="imagem-uploadador">
                      Upload por: {imagem.uploadador.nome}
                    </div>
                  )}
                </div>
                <div className="imagem-actions">
                  <button
                    onClick={() => handleToggleAtivo(imagem.id)}
                    className={`btn ${imagem.ativo ? 'btn-warning' : 'btn-success'}`}
                  >
                    {imagem.ativo ? '⏸️ Desativar' : '▶️ Ativar'}
                  </button>
                  <button
                    onClick={() => handleDeletar(imagem.id)}
                    className="btn btn-danger"
                  >
                    🗑️ Deletar
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ImagensAleatorias;
